import { Button, FormBuilder, toast } from "@k3-tech/react-kit";
import { RotateCcwIcon } from "lucide-react";
import { z } from "zod";
import { usePricelistProductSalesChannelDelete } from "../usecases/usePricelistProductSalesChannelDelete";
import { usePricelistProductSalesChannelUpsert } from "../usecases/usePricelistProductSalesChannelUpsert";

type PricelistProductSalesChannelUpsertFormData = {
	/** Display only */
	channelName: string;
	/** Display only */
	sellingPriceAuto: number;
	sellingPriceCustom?: number | string;
};

// A cleared number input reaches the form as "", null, undefined or NaN depending on the browser.
const isBlankPrice = (value: unknown) => {
	if (value === null || value === undefined) return true;
	if (typeof value === "number") return Number.isNaN(value);
	return String(value).trim() === "";
};

export default function PricelistProductSalesChannelUpsertDialog({
	pricelistProductID,
	salesChannelID,
	channelName,
	autoSellingPrice,
	customSellingPrice,
	pricelistProductSalesChannelID,
	isActive,
	onSuccess,
	onCancel,
}: {
	pricelistProductID: string;
	salesChannelID: string;
	channelName: string;
	autoSellingPrice: number;
	/** Undefined when the channel has no custom price and follows the auto price. */
	customSellingPrice?: number;
	pricelistProductSalesChannelID?: string;
	isActive?: boolean;
	onSuccess: (message: string) => void;
	onCancel: () => void;
}) {
	const { upsertPricelistProductSalesChannel } =
		usePricelistProductSalesChannelUpsert();
	const { deletePricelistProductSalesChannel } =
		usePricelistProductSalesChannelDelete();

	const hasCustomPrice =
		pricelistProductSalesChannelID !== undefined &&
		customSellingPrice !== undefined;

	const clearCustomPrice = async () => {
		if (!pricelistProductSalesChannelID) return;
		const result = await deletePricelistProductSalesChannel(
			pricelistProductSalesChannelID,
		);
		if (!result.error) {
			onSuccess(
				`${channelName} uses the auto price again (${autoSellingPrice.toLocaleString()})`,
			);
		}
	};

	const onSubmit = async (data: PricelistProductSalesChannelUpsertFormData) => {
		const isBlank = isBlankPrice(data.sellingPriceCustom);
		const raw = isBlank ? "" : String(data.sellingPriceCustom).trim();
		if (!isBlank && Number(raw) <= 0) {
			toast.error("Custom price must be greater than 0");
			return;
		}

		// An empty field drops the custom price, so the channel sells at the auto price again.
		if (isBlank) {
			if (!pricelistProductSalesChannelID) {
				onCancel();
				return;
			}
			await clearCustomPrice();
			return;
		}

		const result = await upsertPricelistProductSalesChannel({
			pricelistProductID,
			salesChannelID,
			customSellingPrice: Number(raw),
			isActive: isActive ?? true,
		});
		if (!result.error) {
			onSuccess("Sales Channel Price updated");
		}
	};

	return (
		<FormBuilder<PricelistProductSalesChannelUpsertFormData>
			defaultValues={{
				channelName,
				sellingPriceAuto: autoSellingPrice,
				sellingPriceCustom: customSellingPrice,
			}}
			onSubmit={onSubmit}
			onCancel={onCancel}
			actionsClassName="justify-end"
			showActionsSeparator={false}
			customActions={
				hasCustomPrice ? (
					// FormBuilder fixes the action order, so the reset is pulled left on its own.
					<Button
						type="button"
						variant="outline"
						className="order-first mr-auto"
						onClick={clearCustomPrice}
					>
						<RotateCcwIcon size={16} />
						<span>Reset to Auto Price</span>
					</Button>
				) : undefined
			}
			sections={[
				{
					layout: "grid",
					grid: { cols: 1, smCols: 2, gap: "gap-4" },
					fields: [
						{
							name: "channelName",
							label: "Channel",
							type: "text",
							disabled: true,
							gridCols: 2,
						},
						{
							name: "sellingPriceAuto",
							label: "Selling Price (Auto)",
							type: "number",
							disabled: true,
						},
						{
							name: "sellingPriceCustom",
							label: "Selling Price (Custom)",
							type: "number",
							required: false,
							description:
								"Leave empty to sell at the auto price. A custom price must be greater than 0.",
							// FormBuilder's generated schema rejects every shape an emptied number
							// input takes, which is the one value that has to get through here.
							validation: z
								.any()
								.refine(
									(value) => isBlankPrice(value) || Number(value) > 0,
									"Custom price must be greater than 0",
								),
						},
					],
				},
			]}
			submitLabel="Save"
		/>
	);
}
